import React, { useMemo } from "react";
import PropTypes from "prop-types";
import {
  Button,
  Checkbox,
  FormControl,
  MenuItem,
  Select,
  TableCell,
  TableRow
} from "@mui/material";
import { TimePicker } from "../../time_picker";
import InputOICH from "../../resuable/input_oich";
import styled from "styled-components";
/* eslint-disable react/no-unknown-property -- project-standard Cypress selectors */

export const TIME_FEATURE_FLAG = false;

const RecurringOptions = [
  {
    label: "One time",
    value: "none"
  },
  {
    label: "Every Day",
    value: "ever_day"
  },
  {
    label: "Weekdays",
    value: "weekdays"
  },
  {
    label: "Weekends",
    value: "weekends"
  },
  {
    label: "Monday",
    value: "mon"
  },
  {
    label: "Tuesday",
    value: "tue"
  },
  {
    label: "Wednesday",
    value: "wed"
  },
  {
    label: "Thursday",
    value: "thu"
  },
  {
    label: "Friday",
    value: "fri"
  },
  {
    label: "Saturday",
    value: "sat"
  },
  {
    label: "Sunday",
    value: "sun"
  }
];

export const AppointmentScheduleTableRow = ({ row, onEdit, isNew, onDeleteRow }) => {
  const fieldPrefix = isNew ? "new-peer-schedule-table" : `peer-schedule-table-${row.id}`;

  const onChange = (newValue, valueToUpdate) => {
    onEdit(valueToUpdate, newValue, row && row.id);
  };

  const recurringValueToShow = useMemo(() => {
    if (isNew) return "";
    const option = RecurringOptions.find(
      (recurringOption) => recurringOption.value === row.recurring
    );
    if (option) return option.label;
    return "Not specified";
  }, [row]);

  return (
    <StyledTableRow key={row.name} sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
      {isNew && (
        <StyledTableCell $isNew={isNew} sx={{ minWidth: 200, marginTop: "auto" }} align="right">
          <FormControl fullWidth>
            <label
              className="label"
              htmlFor={`${fieldPrefix}-recurrence`}
              id={`${fieldPrefix}-recurrence-label`}
            >
              Recurrence
            </label>
            <Select
              explorator_test_id={`${fieldPrefix}-recurrence`}
              labelId={`${fieldPrefix}-recurrence-label`}
              id={`${fieldPrefix}-recurrence`}
              value={row.recurring}
              onChange={(event) => onChange(event.target.value, "recurring")}
            >
              {RecurringOptions.map((recurringOption) => (
                <MenuItem key={recurringOption.label} value={recurringOption.value}>
                  {recurringOption.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </StyledTableCell>
      )}
      {!isNew && (
        <StyledTableCell $isNew={isNew} component="th" scope="row">
          {recurringValueToShow}
        </StyledTableCell>
      )}
      <StyledTableCell $isNew={isNew} component="th" scope="row">
        <InputOICH
          id={`${fieldPrefix}-start-date`}
          exploratorTestId={`${fieldPrefix}-start-date`}
          ariaLabel="Start date"
          onChange={(event) => onChange(event.target.value, "start_date")}
          type="date"
          label={isNew ? "Start Date" : undefined}
          value={row.start_date}
        />
      </StyledTableCell>
      <StyledTableCell $isNew={isNew} align="right">
        <InputOICH
          id={`${fieldPrefix}-end-date`}
          exploratorTestId={`${fieldPrefix}-end-date`}
          ariaLabel="End date"
          onChange={(event) => onChange(event.target.value, "end_date")}
          type="date"
          label={isNew ? "End Date" : undefined}
          additionalLabel={"(leave blank to have it last forever)"}
          value={row.end_date}
        />
      </StyledTableCell>
      {TIME_FEATURE_FLAG && (
        <>
          <StyledTableCell $isNew={isNew} align="right">
            <Checkbox
              defaultChecked={row.all_day}
              onChange={(event) => onChange(event, "all_day")}
            />
          </StyledTableCell>
          <StyledTableCell $isNew={isNew} align="right">
            <TimePicker
              disabled={row.all_day}
              onChange={(value) => onChange(value, "start_time")}
              value={row.start_time}
            />
          </StyledTableCell>
          <StyledTableCell $isNew={isNew} align="right">
            <TimePicker
              disabled={row.all_day}
              onChange={(value) => onChange(value, "end_time")}
              value={row.end_time}
            />
          </StyledTableCell>{" "}
        </>
      )}

      <StyledTableCell $isNew={isNew} align="right">
        <InputOICH
          id={`${fieldPrefix}-description`}
          exploratorTestId={`${fieldPrefix}-description`}
          ariaLabel="Time off description"
          debounceMs={1000}
          label={isNew ? "Description" : undefined}
          value={row.description}
          onChange={(event) => onChange(event.target.value, "description")}
        />
      </StyledTableCell>
      {!isNew && (
        <StyledTableCell $isNew={isNew} align="right">
          <Button
            explorator_test_id={`peer-schedule-delete-${row.id}`}
            onClick={() => onDeleteRow(row.id)}
            color="error"
          >
            Delete
          </Button>
        </StyledTableCell>
      )}
    </StyledTableRow>
  );
};

AppointmentScheduleTableRow.propTypes = {
  row: PropTypes.object,
  onEdit: PropTypes.func,
  isNew: PropTypes.bool,
  onDeleteRow: PropTypes.func
};

const StyledTableRow = styled(TableRow)`
  && {
    padding: 0.2rem;
  }

  && .input.valid {
    min-height: var(--ccw-control-height);
    padding: 0.55rem 0.7rem;
    color: var(--ccw-color-ink);
    background: #ffffff;
    border: 1px solid var(--ccw-color-border-strong);
    border-radius: var(--ccw-control-radius);
    box-shadow: none;
  }

  && .MuiOutlinedInput-root {
    min-height: var(--ccw-control-height);
    background: #ffffff;
    border-radius: var(--ccw-control-radius);
    box-shadow: none;
  }
`;

const StyledTableCell = styled(TableCell)`
  && {
    border: ${({ $isNew }) => ($isNew ? "none" : "")};
  }
`;

export const AppointmentScheduleCardRow = ({ row, onEdit, isNew, onDeleteRow }) => {
  const fieldPrefix = isNew ? "new-peer-schedule-mobile" : `peer-schedule-mobile-${row.id}`;

  const onChange = (newValue, valueToUpdate) => {
    onEdit(valueToUpdate, newValue, row && row.id);
  };

  const recurringLabel = useMemo(() => {
    if (!row?.recurring) return "-";
    const option = RecurringOptions.find((item) => item.value === row.recurring);
    return option ? option.label : row.recurring;
  }, [row?.recurring]);

  return (
    <MobileCard>
      {isNew ? (
        <FieldGroup>
          <FieldLabel htmlFor={`${fieldPrefix}-recurrence`} id={`${fieldPrefix}-recurrence-label`}>
            Recurrence
          </FieldLabel>
          <Select
            explorator_test_id={`${fieldPrefix}-recurrence`}
            id={`${fieldPrefix}-recurrence`}
            labelId={`${fieldPrefix}-recurrence-label`}
            value={row.recurring}
            onChange={(event) => onChange(event.target.value, "recurring")}
            fullWidth
          >
            {RecurringOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FieldGroup>
      ) : (
        <FieldGroup>
          <FieldLabel as="span">Recurrence</FieldLabel>
          <FieldValue>{recurringLabel}</FieldValue>
        </FieldGroup>
      )}

      <FieldGroup>
        <FieldLabel htmlFor={`${fieldPrefix}-start-date`} id={`${fieldPrefix}-start-date-label`}>
          Start Date
        </FieldLabel>
        <InputOICH
          id={`${fieldPrefix}-start-date`}
          exploratorTestId={`${fieldPrefix}-start-date`}
          ariaLabelledBy={`${fieldPrefix}-start-date-label`}
          onChange={(event) => onChange(event.target.value, "start_date")}
          type="date"
          value={row.start_date}
        />
      </FieldGroup>

      <FieldGroup>
        <FieldLabel htmlFor={`${fieldPrefix}-end-date`} id={`${fieldPrefix}-end-date-label`}>
          End Date
        </FieldLabel>
        <InputOICH
          id={`${fieldPrefix}-end-date`}
          exploratorTestId={`${fieldPrefix}-end-date`}
          ariaLabelledBy={`${fieldPrefix}-end-date-label`}
          onChange={(event) => onChange(event.target.value, "end_date")}
          type="date"
          additionalLabel={isNew ? "Leave blank for ongoing" : undefined}
          value={row.end_date}
        />
      </FieldGroup>

      <FieldGroup>
        <FieldLabel htmlFor={`${fieldPrefix}-description`} id={`${fieldPrefix}-description-label`}>
          Description
        </FieldLabel>
        <InputOICH
          id={`${fieldPrefix}-description`}
          exploratorTestId={`${fieldPrefix}-description`}
          ariaLabelledBy={`${fieldPrefix}-description-label`}
          debounceMs={1000}
          value={row.description}
          onChange={(event) => onChange(event.target.value, "description")}
        />
      </FieldGroup>

      {!isNew && (
        <MobileActions>
          <Button
            explorator_test_id={`peer-schedule-delete-${row.id}`}
            onClick={() => onDeleteRow(row.id)}
            color="error"
            size="small"
          >
            Delete
          </Button>
        </MobileActions>
      )}
    </MobileCard>
  );
};

AppointmentScheduleCardRow.propTypes = {
  row: PropTypes.object,
  onEdit: PropTypes.func,
  isNew: PropTypes.bool,
  onDeleteRow: PropTypes.func
};

const MobileCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem;
  border-radius: 0.75rem;
  border: 1px solid #dfe3eb;
  background-color: #fff;

  .input.valid {
    width: 100%;
    min-height: var(--ccw-control-height);
    padding: 0.55rem 0.7rem;
    color: var(--ccw-color-ink);
    background: #ffffff;
    border: 1px solid var(--ccw-color-border-strong);
    border-radius: var(--ccw-control-radius);
    box-shadow: none;
  }

  .MuiOutlinedInput-root {
    min-height: var(--ccw-control-height);
    background: #ffffff;
    border-radius: var(--ccw-control-radius);
    box-shadow: none;
  }
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

const FieldLabel = styled.label`
  font-size: 1rem;
  font-weight: 600;
  color: #475569;
`;

const FieldValue = styled.span`
  font-size: 1rem;
`;

const MobileActions = styled.div`
  display: flex;
  justify-content: flex-end;
`;
